import { AppBar, Card, CardContent, Divider, FormControl, FormControlLabel, FormGroup, FormHelperText, Input, InputLabel, MenuItem, Select, Switch, TextField, Toolbar } from "@mui/material";
import Grid from "@mui/material/Grid";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { SquarePicture } from "assets/appConfig";
import { AppImages } from "assets/appResources";
import { FormStyleClasses, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonBack from "components/Buttons/ButtonBack";
import ButtonSave from "components/Buttons/ButtonSave";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import { ImageSelection } from "components/FileUpload/ImageSelection";
import IconTrophy from "components/Icons/IconTrophy";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiKeyboardDatePicker from "components/MuiExtras/MuiKeyboardDatePicker";
import MuiPaper from "components/MuiExtras/MuiPaper";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldCounter from "components/TextFieldCounter";
import TextFieldNumeric from "components/TextFieldNumeric";
import BadgeRuleActionName from "components/Translations/BadgeRuleActionName";
import BadgeRuleFrequenceName from "components/Translations/BadgeRuleFrequenceName";
import BadgeRulePeriodName from "components/Translations/BadgeRulePeriodName";
import LibUtils from "lib/LibUtils";
import MediaUtils from "lib/MediaUtils";
import { BadgeRuleAction, BadgeRuleFrequence, BadgeRulePeriod } from "models/ApiEnums";
import { GetBadgeDto, SaveBadgeDto, TopicSuggestionDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import { Moment } from "moment";
import * as React from "react";
import { withTranslation, WithTranslation } from 'react-i18next';
import { Prompt } from "react-router";
import { BadgeAbout } from "views/Gamification/Components/BadgeAbout";
import BadgeLogDialog from "views/Gamification/Dialogs/BadgeLogDialog";
import CourseAutoComplete from "views/Publication/Components/CourseAutoComplete";
import TopicAutoComplete from "views/Topic/Components/TopicAutoComplete";

/////////////////////////////////////////////////////////////////////////////////////

interface MaxUserOptionsItem {
    value: string;
    text: React.ReactElement;
}

interface IProps {
    badgeId?: string;
    onClose: (reload: boolean) => void;
}

interface IState {
    dialog: any,
    formCycle: RequestLifeCycle,
    lastError: any,
    record: Partial<GetBadgeDto>,
    validate: {
        description: boolean,
        name: boolean,
        nameUnique: boolean,
        points: boolean,
        thumbnail: boolean,
    };
}

type Props = IProps & WithTranslation & WithStyles<typeof FormStyleClasses> & WithSnackbar;

/////////////////////////////////////////////////////////////////////////////////////

interface IRuleActions {
    action: BadgeRuleAction;
    howManyLabel?: string;
    showHowMany: boolean;
    showPeriod: boolean;
    showFrequency: boolean;
    showMaxUsers: boolean;
    showTopic: 0 | 1 | 2;
    showCourses: boolean;
}

const ruleActions: IRuleActions[] = [
    { action: BadgeRuleAction.CreatePostArticleQuestion, showFrequency: true, showHowMany: true, showMaxUsers: true, showPeriod: true, showTopic: 1, showCourses: false },
    { action: BadgeRuleAction.AnswerQuestion, showFrequency: true, showHowMany: true, showMaxUsers: true, showPeriod: true, showTopic: 1, showCourses: false },
    { action: BadgeRuleAction.TestSessionComplete, showFrequency: true, showHowMany: true, showMaxUsers: true, showPeriod: true, showTopic: 1, showCourses: false },
    { action: BadgeRuleAction.CourseComplete, showFrequency: true, showHowMany: true, showMaxUsers: true, showPeriod: true, showTopic: 1, showCourses: false },
    { action: BadgeRuleAction.CourseListComplete, showFrequency: false, showHowMany: false, showMaxUsers: false, showPeriod: false, showTopic: 0, showCourses: true },
    { action: BadgeRuleAction.HadPublicationHighlight, showFrequency: true, showHowMany: true, showMaxUsers: true, showPeriod: true, showTopic: 1, showCourses: false },
    { action: BadgeRuleAction.HadPublicationThanked, showFrequency: true, showHowMany: true, showMaxUsers: true, showPeriod: true, showTopic: 0, showCourses: false },
    { action: BadgeRuleAction.HadPublicationLikes, showFrequency: true, showHowMany: true, showMaxUsers: true, showPeriod: true, showTopic: 1, showCourses: false },
    { action: BadgeRuleAction.RankingMonthTop3, showFrequency: false, showHowMany: false, showMaxUsers: false, showPeriod: false, showTopic: 0, showCourses: false },
    { action: BadgeRuleAction.ReachNumberOfRankingPoints, showFrequency: true, showHowMany: true, showMaxUsers: true, showPeriod: true, showTopic: 0, showCourses: false, howManyLabel: "Quantos pontos?" },
    { action: BadgeRuleAction.GetMinFollowers, showFrequency: false, showHowMany: true, showMaxUsers: false, showPeriod: false, showTopic: 0, showCourses: false, howManyLabel: "Quantos?" },
    { action: BadgeRuleAction.AcquireTopicSpecialist, showFrequency: false, showHowMany: false, showMaxUsers: false, showPeriod: false, showTopic: 2, showCourses: false },
];

/////////////////////////////////////////////////////////////////////////////////////

class BadgeEdit extends React.Component<Props, IState> {

    private loadDto: any = null;
    private unsaveMessage: string;
    private apiGetSuggestions = api.getBadgeTopicSuggestions();

    constructor(props: Props) {
        super(props);

        this.unsaveMessage = props.t("Common.Question.Unsave");

        this.loadDto = null;

        this.state = {
            formCycle: RequestLifeCycle.Init,
            lastError: null,
            dialog: null,
            record: {
                badgeId: null,
                description: "",
                name: "",
                isActive: true,
                points: 1,
                thumbnail: null,

                availableEndDate: null,
                availableStartDate: null,

                applyRuleInValidPeriodOnly: true,
                ruleAction: ruleActions[0].action,
                ruleHowMany: 1,
                ruleFrequence: BadgeRuleFrequence.NoLimit,
                rulePeriod: BadgeRulePeriod.NoDeadline,
                ruleMaxUsers: 0,
                ruleMaxUsersPerPeriod: false,
                ruleTopicId: null,

                recordInfo: null,
                counters: null,
            },
            validate: {
                description: null,
                name: null,
                nameUnique: null,
                points: true,
                thumbnail: true,
            },
        };

    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Gamification.Components.BadgeEdit." + r, f);
    }

    ///
    private init() {
        if (this.props.badgeId != null && this.props.badgeId != "create")
            this.continueLoad(this.props.badgeId);
        else {
            this.loadDto = LibUtils.deepCopy(this.state.record);
            this.setState({ formCycle: RequestLifeCycle.InitSuccess });
        }
    }

    ///
    private continueLoad(badgeId: string) {
        api.getBadgeRecord(badgeId)
            .then((dto) => {

                this.loadDto = LibUtils.deepCopy(dto);

                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess,
                    record: dto,
                    validate: {
                        name: true,
                        description: true,
                        nameUnique: true,
                        points: true,
                        thumbnail: true,
                    }
                });
            })
            .catch((err: any) => {
                this.setState({
                    lastError: err,
                    formCycle: RequestLifeCycle.InitFailed
                });
            });
    }

    ///
    private isValidForm(): boolean {
        if (!LibUtils.isAllPropertiesWithValue(this.state.validate, true)) return false;

        const { record } = this.state;
        const actionConfig = ruleActions.find(p => p.action == record.ruleAction);
        if (actionConfig.showFrequency && record.ruleFrequence == null) return false;
        if (actionConfig.showHowMany && record.ruleHowMany < 1) return false;
        if (actionConfig.showMaxUsers && record.ruleMaxUsers == null) return false;
        if (actionConfig.showPeriod && record.rulePeriod == null) return false;
        if (actionConfig.showTopic == 2 && record.ruleTopicId == null) return false;

        return true;
    }

    ///
    private getPostDto(): SaveBadgeDto {
        const { record } = this.state;

        return {
            badgeId: record.badgeId,
            description: record.description,
            name: record.name,
            isActive: record.isActive,
            points: record.points,
            thumbnail: record.thumbnail == null ? null : { fileId: record.thumbnail.fileId },

            availableEndDate: record.availableEndDate,
            availableStartDate: record.availableStartDate,

            applyRuleInValidPeriodOnly: record.applyRuleInValidPeriodOnly,
            ruleAction: record.ruleAction,
            ruleFrequence: record.ruleFrequence,
            ruleHowMany: record.ruleHowMany,
            ruleMaxUsers: record.ruleMaxUsers,
            ruleMaxUsersPerPeriod: record.ruleMaxUsersPerPeriod,
            rulePeriod: record.rulePeriod,
            ruleTopicId: record.ruleTopicId == null ? null : record.ruleTopicId.key,
            ruleCourses: record.ruleCourses == null ? null : record.ruleCourses.map((v) => v.key)
        };
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {

        this.setState(state => {

            switch (name) {
                case "name":
                    state.record.name = v;
                    state.validate.name = v != "";
                    break;
                case "description":
                    state.record.description = v;
                    state.validate.description = v != "";
                    break;
                case "points":
                    state.record.points = v;
                    state.validate.points = v > 0;
                    break;
                case "thumbnail":
                    if (v == null) state.record.thumbnail = null;
                    else state.record.thumbnail = { fileId: v.fileId, url: v.tempUrl };
                    break;
                case "thumbnailUploading":
                    state.validate.thumbnail = !v;
                    break;

                case "rulePeriod":
                    state.record.rulePeriod = v;

                    if (state.record.rulePeriod != BadgeRulePeriod.NoDeadline) {
                        state.record.ruleFrequence = BadgeRuleFrequence.OncePerPeriod;
                    }
                    else {
                        state.record.ruleFrequence = state.record.ruleMaxUsers == 0 ? BadgeRuleFrequence.NoLimit : BadgeRuleFrequence.Once;
                    }
                    break;

                case "ruleMaxUsers":
                    {
                        const maxUsers = parseInt(v);
                        state.record.ruleMaxUsers = maxUsers;
                        state.record.ruleMaxUsersPerPeriod = state.record.ruleFrequence == BadgeRuleFrequence.OncePerPeriod;
                        if (maxUsers > 0 && state.record.ruleFrequence == BadgeRuleFrequence.NoLimit) {
                            state.record.ruleFrequence = BadgeRuleFrequence.Once;
                        }
                    }
                    break;

                case "ruleAction":
                    if (state.record.ruleAction == BadgeRuleAction.AcquireTopicSpecialist && (v as BadgeRuleAction) != BadgeRuleAction.AcquireTopicSpecialist) {
                        state.record.ruleTopicId = null;
                    }
                    state.record.ruleAction = v;
                    break;

                case "ruleCourses":
                    state.record.ruleCourses = v;
                    break;

                default:
                    state.record[name] = v;
                    break;
            }

            return state;
        });
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (this.isValidForm()) {

            const dto = this.getPostDto();

            this.setState({
                formCycle: RequestLifeCycle.Posting,
            });

            api.saveBadgeRecord(dto)
                .then(() => {
                    this.props.snackbar.showMessage(this.res("UpdateSuccess"), "success");
                    this.props.onClose(true);
                })
                .catch((err: any) => {
                    this.setState({ lastError: err, formCycle: RequestLifeCycle.PostFailed });
                });
        }
    }

    ///
    private isRecordChanged() {
        return this.state.lastError == null && !LibUtils.deepCompare(this.state.record, this.loadDto);
    }

    ///
    private checkName = () => {
        api.checkBadgeNameIsUnique(this.state.record.badgeId, this.state.record.name)
            .then((result) => {
                this.setState(state => {
                    state.validate.nameUnique = result;
                    return state;
                });
            });
    };

    ///
    private renderToolbar() {
        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar}>
                <Toolbar>

                    <div className={GlobalClassKeys.grow}>
                        <ButtonBack
                            text={this.props.t("Common.Buttons.BackText")}
                            onClick={() => {
                                if (this.isRecordChanged()) {
                                    if (window.confirm(this.unsaveMessage)) {
                                        this.props.onClose(false);
                                    }
                                }
                                else {
                                    this.props.onClose(false);
                                }
                            }}
                        />
                    </div>

                    <ButtonSave
                        disabled={!this.isValidForm()}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                    />
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderRecordInfo() {
        return this.state.record.recordInfo
            ? (
                <MuiContainer margin="normal">
                    <BadgeAbout
                        record={this.state.record.recordInfo}
                        counters={this.state.record.counters}
                        onShowHistory={
                            () => {
                                this.setState({
                                    dialog:
                                        <BadgeLogDialog
                                            badgeId={this.state.record.badgeId}
                                            onClose={() => this.setState({ dialog: null })}
                                        />
                                });
                            }
                        }
                    />
                </MuiContainer>
            )
            : null;
    }

    ///
    private renderFormMain() {

        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal" >
                <form noValidate autoComplete="off" >

                    <MuiPaper>

                        <Grid container spacing={2}>
                            <Grid item style={{ width: 124 }}>
                                <ImageSelection
                                    acceptedFiles={MediaUtils.AcceptImages()}
                                    allowEditSelectedImage={true}
                                    currentImageUrl={record && record.thumbnail ? record.thumbnail.url : null}
                                    dimensions={SquarePicture}
                                    initialSource="local"
                                    emptyImageUrl={AppImages.DefaultBadgeThumbnail}
                                    enableClear={true}
                                    onClear={() => { this.updateRecord("thumbnail", null); }}
                                    onFileUploadStarted={() => this.updateRecord("thumbnailUploading", true)}
                                    onFileUploadFinished={() => this.updateRecord("thumbnailUploading", false)}
                                    onFileUploadSuccess={(data) => this.updateRecord("thumbnail", data.thumbnail ? data.thumbnail : data.mainFile)}
                                />

                            </Grid>

                            <Grid item xs>

                                <FormGroup>
                                    <TextField
                                        autoFocus
                                        name="name"
                                        label={this.res("NameLabel")}
                                        autoComplete="off"
                                        error={validate.name === false || validate.nameUnique === false}
                                        margin="dense"
                                        value={record.name || ""}
                                        onChange={(e) => this.handleChange(e as React.ChangeEvent<HTMLInputElement>)}
                                        onBlur={this.checkName}
                                        fullWidth
                                        required
                                        inputProps={{
                                            maxLength: 255,
                                        }}
                                    />
                                    {validate.nameUnique === false && <FormHelperText error>{this.res("NameAlreadyExistsMessage")}</FormHelperText>}
                                </FormGroup>

                                <FormGroup>
                                    <TextFieldCounter
                                        name="description"
                                        label={this.props.t<string>("Common.Literals.DescriptionLabel")}
                                        margin="dense"
                                        value={record.description || ""}
                                        onChange={(v) => this.updateRecord("description", v)}
                                        maxLength={1500}
                                        fullWidth
                                        multiline
                                        required
                                    />
                                </FormGroup>

                                <Grid container spacing={1}>

                                    <Grid item xs={6}>
                                        <MuiKeyboardDatePicker
                                            fullWidth
                                            clearable
                                            margin="dense"
                                            label={this.props.t<string>("Common.Literals.DateAvailableStartLabel")}
                                            placeholder={this.props.t("Common.Literals.DateImmediately")}
                                            value={record.availableStartDate == null ? null : record.availableStartDate}
                                            InputLabelProps={
                                                {
                                                    shrink: true
                                                }
                                            }
                                            onChange={
                                                (date) => {
                                                    if (date == null) {
                                                        this.updateRecord("availableStartDate", null);
                                                    }
                                                    else {
                                                        date.set({ hour: 0, minute: 0, second: 0 });
                                                        this.updateRecord("availableStartDate", date.toDate());
                                                    }
                                                }
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <MuiKeyboardDatePicker
                                            fullWidth
                                            clearable
                                            margin="dense"
                                            label={this.props.t<string>("Common.Literals.DateAvailableEndLabel")}
                                            placeholder={this.props.t("Common.Literals.DateUnlimited")}
                                            value={record.availableEndDate == null ? null : record.availableEndDate}
                                            InputLabelProps={
                                                {
                                                    shrink: true
                                                }
                                            }
                                            onChange={
                                                (date: Moment) => {
                                                    if (date == null) {
                                                        this.updateRecord("availableEndDate", null);
                                                    }
                                                    else {
                                                        date.set({ hour: 23, minute: 59, second: 59 });
                                                        this.updateRecord("availableEndDate", date.toDate());
                                                    }
                                                }
                                            }
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={1} alignItems="flex-end">

                                    <Grid item xs={12} md={6}>

                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item>
                                                <IconTrophy size="2x" />
                                            </Grid>
                                            <Grid item>
                                                <FormControl fullWidth margin="dense">
                                                    <TextFieldNumeric
                                                        name="points"
                                                        label={this.res("PointsLabel")}
                                                        allowNegative={false}
                                                        error={validate.points === false}
                                                        margin="dense"
                                                        value={record.points || "1"}
                                                        onChange={this.handleChange}
                                                        fullWidth
                                                        required
                                                    />
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <FormGroup>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        name="isActive"
                                                        checked={record.isActive}
                                                        onChange={this.handleChange}
                                                        value="isActive"
                                                        color="secondary"
                                                    />
                                                }
                                                label={this.res("ActiveLabel")}
                                                labelPlacement="start"
                                            />
                                        </FormGroup>
                                    </Grid>

                                </Grid>


                            </Grid>
                        </Grid>

                    </MuiPaper>

                </form >
            </MuiContainer >
        );
    }

    ///
    private getMaxUserOptions() {

        var result: MaxUserOptionsItem[] = [];

        result.push({ value: "0", text: <span><BadgeRuleFrequenceName value={BadgeRuleFrequence.NoLimit} /></span> });
        result.push({ value: "-", text: <span></span > });
        result.push({ value: "1", text: <span>{this.res("GoalText", { count: 1 })}</span> });
        result.push({ value: "3", text: <span>{this.res("GoalText", { count: 3 })}</span> });
        result.push({ value: "5", text: <span>{this.res("GoalText", { count: 5 })}</span> });
        result.push({ value: "10", text: <span>{this.res("GoalText", { count: 10 })}</span> });
        result.push({ value: "20", text: <span>{this.res("GoalText", { count: 20 })}</span> });
        result.push({ value: "50", text: <span>{this.res("GoalText", { count: 50 })}</span> });
        return result;
    }

    ///
    private getTopicSuggestions = (userId: string, searchText: string, skipRecords: number, takeRecords: number): Promise<TopicSuggestionDto[]> => {
        return this.apiGetSuggestions.execute({
            userId,
            searchText,
            offset: skipRecords,
            take: takeRecords
        });
    };

    ///
    private renderRule() {

        const { record } = this.state;

        const action = record.ruleAction;
        const actionConfig = ruleActions.find(p => p.action == action);
        const byMonthOrWeek = record.rulePeriod == BadgeRulePeriod.Month || record.rulePeriod == BadgeRulePeriod.Week;

        return (
            <MuiContainer margin="normal" >

                <Card>
                    <CardHeaderColor title={this.res("RuleTitle")} color="primary" />

                    <CardContent>

                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel shrink htmlFor="LabelRuleAction">{this.res("RuleActionLabel")}</InputLabel>
                                    <Select
                                        autoWidth
                                        displayEmpty
                                        name="ruleAction"
                                        value={action}
                                        onChange={(e) => this.handleChange(e as React.ChangeEvent<HTMLInputElement>)}
                                        input={<Input id="LabelRuleAction" />}
                                    >
                                        {ruleActions.map((v) => (
                                            <MenuItem
                                                key={v.action}
                                                value={v.action}
                                            >
                                                <BadgeRuleActionName value={v.action} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs>
                                {
                                    actionConfig.showHowMany &&
                                    <FormControl fullWidth margin="dense">
                                        <TextFieldNumeric
                                            name="ruleHowMany"
                                            label={actionConfig.howManyLabel || this.res("RuleHowManyLabel")}
                                            margin="dense"
                                            error={record.ruleHowMany < 1}
                                            value={record.ruleHowMany}
                                            onChange={this.handleChange}
                                            allowNegative={false}
                                            fullWidth
                                            required
                                        />
                                    </FormControl>
                                }
                            </Grid>

                            <Grid item xs>
                                {
                                    actionConfig.showPeriod &&
                                    <FormControl fullWidth margin="dense">
                                        <InputLabel shrink htmlFor="LabelRulePeriod">{this.res("RulePeriodLabel")}</InputLabel>
                                        <Select
                                            autoWidth
                                            displayEmpty
                                            name="rulePeriod"
                                            value={record.rulePeriod}
                                            onChange={(e) => this.handleChange(e as React.ChangeEvent<HTMLInputElement>)}
                                            input={<Input id="LabelRulePeriod" />}
                                        >
                                            <MenuItem value={BadgeRulePeriod.NoDeadline}><BadgeRulePeriodName value={BadgeRulePeriod.NoDeadline} /></MenuItem>
                                            <MenuItem value={BadgeRulePeriod.Week}><BadgeRulePeriodName value={BadgeRulePeriod.Week} /></MenuItem>
                                            <MenuItem value={BadgeRulePeriod.Month}><BadgeRulePeriodName value={BadgeRulePeriod.Month} /></MenuItem>
                                        </Select>
                                    </FormControl>
                                }
                            </Grid>
                        </Grid>


                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs>
                                {
                                    actionConfig.showFrequency &&
                                    <FormControl fullWidth margin="dense">
                                        <InputLabel shrink htmlFor="LabelRuleFrequence">{this.res("RuleFrequenceLabel")}</InputLabel>
                                        <Select
                                            autoWidth
                                            displayEmpty
                                            name="ruleFrequence"
                                            value={record.ruleFrequence}
                                            onChange={(e) => this.handleChange(e as React.ChangeEvent<HTMLInputElement>)}
                                            input={<Input id="LabelRuleFrequence" />}
                                        >
                                            <MenuItem disabled={byMonthOrWeek} value={BadgeRuleFrequence.NoLimit}><BadgeRuleFrequenceName value={BadgeRuleFrequence.NoLimit} /></MenuItem>
                                            <MenuItem disabled={byMonthOrWeek} value={BadgeRuleFrequence.Once}><BadgeRuleFrequenceName value={BadgeRuleFrequence.Once} /></MenuItem>
                                            <MenuItem disabled={!byMonthOrWeek} value={BadgeRuleFrequence.OncePerPeriod}><BadgeRuleFrequenceName value={BadgeRuleFrequence.OncePerPeriod} /></MenuItem>
                                        </Select>
                                    </FormControl>
                                }
                            </Grid>

                            <Grid item xs>
                                {
                                    actionConfig.showMaxUsers &&
                                    <FormControl fullWidth margin="dense">
                                        <InputLabel shrink htmlFor="LabelRuleMaxUsers">{this.res("RuleMaxUsersLabel")}</InputLabel>
                                        <Select
                                            autoWidth
                                            displayEmpty
                                            name="ruleMaxUsers"
                                            value={record.ruleMaxUsers}
                                            onChange={(e) => this.handleChange(e as React.ChangeEvent<HTMLInputElement>)}
                                            input={<Input id="LabelRuleMaxUsers" />}
                                        >
                                            {
                                                this.getMaxUserOptions().map((v, index) => {
                                                    if (v.value == "-") return <Divider key={index} />;
                                                    return <MenuItem key={index} value={v.value}>{v.text}</MenuItem>;
                                                })
                                            }
                                        </Select>
                                    </FormControl>
                                }
                            </Grid>
                        </Grid>

                        {
                            actionConfig.showTopic != 0 &&
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs>
                                    <TopicAutoComplete
                                        label={this.res("TopicLabel")}
                                        margin="dense"
                                        multiple={false}
                                        placeholder={this.props.t("Common.Literals.TopicSelection")}
                                        selectedItems={record.ruleTopicId == null ? null : [record.ruleTopicId]}
                                        onChange={
                                            (topics) => {
                                                this.updateRecord("ruleTopicId", topics.length == 0 ? null : topics[0]);
                                            }
                                        }
                                    />
                                </Grid>
                            </Grid>
                        }

                        {
                            actionConfig.showCourses &&
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs>
                                    <CourseAutoComplete
                                        label={this.res("CourseLabel")}
                                        margin="dense"
                                        multiple
                                        placeholder={this.props.t("Common.Literals.CourseSelection")}
                                        selectedItems={record.ruleCourses == null ? null : record.ruleCourses}
                                        onChange={
                                            (courses) => {
                                                this.updateRecord("ruleCourses", courses.length == 0 ? null : courses);
                                            }
                                        }
                                    />

                                </Grid>
                            </Grid>
                        }

                    </CardContent>

                </Card>

            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.init();
    }

    ///
    public componentWillUnmount() {
        window.onbeforeunload = undefined;
    }

    ///
    public componentDidUpdate() {
        if (this.isRecordChanged()) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = undefined;
        }
    }

    ///
    public render() {
        if (this.state.formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator type="waiting" />;
        }

        if (this.state.formCycle == RequestLifeCycle.InitFailed) {
            return <RequestErrorReport
                error={this.state.lastError}
                action={
                    <ButtonBack
                        text={this.props.t("Common.Buttons.BackText")}
                        onClick={() => this.props.onClose(false)}
                    />
                }
            />;
        }

        return (
            <Grid container justifyContent="space-around" >
                <Grid item xs>
                    <div className={this.props.classes.formContainer}>

                        {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                        <RequestErrorDialog
                            open={this.state.formCycle == RequestLifeCycle.PostFailed}
                            error={this.state.lastError}
                            onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                        />

                        {this.state.dialog}
                        {this.renderToolbar()}
                        {this.renderFormMain()}
                        {this.renderRule()}
                        {this.renderRecordInfo()}

                        <Prompt
                            when={this.isRecordChanged()}
                            message={this.unsaveMessage}
                        />

                    </div>
                </Grid>
            </Grid>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(BadgeEdit)));